import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Box,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { RuleTemplateModal } from '../RuleTemplateModal/RuleTemplateModal';

import { useRuleService } from '../../../../../../context/RuleServiceContext';
import { getOperators, RuleTenant } from '../../../../../../helper/RuleServiceV2.const';
import { RuleServiceCommand } from '../../../../../../../../services/command';
import Swal from 'sweetalert2';
import { RTTPG } from './ruleTemplateGrid.classes';

export const RuleTemplateGrid: FC<any> = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRuleTemplate, setSelectedRuleTemplate] = useState<any>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editRuleTemplate, setEditRuleTemplate] = useState<any>(null);

    const { t } = useTranslation();
    const { gridData, tenant, totalCount, setPage, page, rowsPerPage, setRowsPerPage, fetchRuleTemplateGridData } =
        useRuleService();

    const operators = getOperators();

    const handleChangePage = (_: any, newPage: any) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handleOpenDialog = (ruleTemplate: any) => {
        setSelectedRuleTemplate(ruleTemplate);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRuleTemplate(null);
    };

    const handleEditRuleTemplate = (ruleTemplate: any) => {
        setEditRuleTemplate(ruleTemplate);
        setIsEditModalOpen(true);
    };

    const handleDeleteRuleTemplate = async (id: number) => {
        const result = await Swal.fire({
            title: t('general.delete'),
            text: t('general.deleteConfirmationText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.no'),
        });
        if (result.isConfirmed) {
            try {
                const res = await RuleServiceCommand.DeleteRuleTemplate({ Id: id });
                if (res && res.statuscode !== 200 && res.StatusCode !== 200) {
                    Swal.fire(t('general.error'), res?.error || res?.message || t('general.couldntDeleted'), 'error');
                    return;
                }
                Swal.fire(t('general.success'), t('general.deleteOperationSuccessMessage'), 'success');
                fetchRuleTemplateGridData();
            } catch (error: any) {
                const errorMsg =
                    error?.data?.error || error?.data?.message || error?.message || t('general.couldntDeleted');
                Swal.fire(t('general.error'), errorMsg, 'error');
            }
        }
    };

    useEffect(() => {
        fetchRuleTemplateGridData();
    }, [tenant, rowsPerPage, page, fetchRuleTemplateGridData]);

    return (
        <>
            <Paper className={RTTPG.tablePaper}>
                <Table className={RTTPG.table}>
                    <TableHead className={RTTPG.head}>
                        <TableRow className={RTTPG.headRow}>
                            <TableCell className={RTTPG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.ruleTemplate.id.name')}
                            </TableCell>
                            <TableCell className={RTTPG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.ruleTemplate.domain.name')}
                            </TableCell>
                            <TableCell className={RTTPG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.ruleTemplate.name.name')}
                            </TableCell>
                            <TableCell className={RTTPG.headCell} style={{ textAlign: 'center' }}>
                            {t('ruleManagement.fields.ruleTemplate.parameterCount.name')}
                            </TableCell>
                            <TableCell className={RTTPG.headCell} style={{ textAlign: 'center' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className={RTTPG.body}>
                        {(gridData || []).map((type: any, key: number) => (
                            <TableRow className={RTTPG.row} key={key} data-rule-template-id={type.id}>
                                <TableCell className={[RTTPG.cell, RTTPG.cellId].join(' ')} style={{ textAlign: 'center' }}>
                                    {type.id}
                                </TableCell>
                                <TableCell className={[RTTPG.cell, RTTPG.cellTenant].join(' ')} style={{ textAlign: 'center' }}>
                                {RuleTenant[type.tenant as keyof typeof RuleTenant]}
                                </TableCell>
                                <TableCell className={[RTTPG.cell, RTTPG.cellName].join(' ')} style={{ textAlign: 'center' }}>
                                    {type.name}
                                </TableCell>
                                <TableCell className={[RTTPG.cell, RTTPG.cellCriteriaCount].join(' ')} style={{ textAlign: 'center' }}>
                                {type.criteriaTypes?.length > 0 && (
                                    <Chip
                                        className={RTTPG.criteriaChip}
                                        label={`${type.criteriaTypes?.length}`}
                                        onClick={() => handleOpenDialog(type)}
                                        color="warning"
                                    />
                                )}
                                </TableCell>

                                <TableCell className={[RTTPG.cell, RTTPG.cellActions].join(' ')} style={{ textAlign: 'center' }}>
                                    <IconButton className={RTTPG.editButton} color="primary" onClick={() => handleEditRuleTemplate(type)}>
                                        <EditIcon className={RTTPG.editIcon} sx={{ width: '16px', height: '16px' }} />
                                </IconButton>
                                    <IconButton className={RTTPG.deleteButton} color="error" onClick={() => handleDeleteRuleTemplate(type.id)}>
                                        <DeleteOutlineIcon className={RTTPG.deleteIcon} sx={{ width: '16px', height: '16px' }} />
                                </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
            <TablePagination
                className={RTTPG.pagination}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <Dialog className={RTTPG.detailsDialog} open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle className={RTTPG.detailsDialogTitle}>
                    <Box className={RTTPG.detailsDialogTitleLayout} sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', width: '100%' }}>
                        {t('ruleManagement.fields.ruleTemplate.details.name') + ' - '}
                        <span className={RTTPG.detailsDialogTitleName} style={{ color: 'blue' }}>{`(${selectedRuleTemplate?.name || ''})`}</span>
                        <IconButton className={RTTPG.detailsDialogCloseButton} sx={{ marginLeft: 'auto' }}>
                            <CloseIcon className={['svg_icons', RTTPG.detailsDialogCloseIcon].filter(Boolean).join(' ')} onClick={handleCloseDialog} />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={RTTPG.detailsDialogContent}>
                    {selectedRuleTemplate && (
                        <Table className={RTTPG.detailsTable}>
                            <TableHead className={RTTPG.detailsHead}>
                                <TableRow className={RTTPG.detailsHeadRow}>
                                    <TableCell className={RTTPG.detailsHeadCell} style={{ textAlign: 'center' }}>
                                        {t('ruleManagement.fields.ruleTemplate.parameterName')}
                                    </TableCell>
                                    <TableCell className={RTTPG.detailsHeadCell} style={{ textAlign: 'center' }}>
                                        {t('ruleManagement.operatorV2')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={RTTPG.detailsBody}>
                                {selectedRuleTemplate.criteriaTypes.map((item: any, index: number) => (
                                    <TableRow className={RTTPG.detailsRow} key={index} data-criteria-index={index}>
                                        <TableCell className={RTTPG.detailsCell} style={{ textAlign: 'center' }}>
                                            {item.name}
                                        </TableCell>
                                        <TableCell className={RTTPG.detailsCell} style={{ textAlign: 'center' }}>
                                            {operators?.[item.operator]?.label}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>
            <RuleTemplateModal
                isModalOpen={isEditModalOpen}
                setIsModalOpen={setIsEditModalOpen}
                initialData={editRuleTemplate}
            />
        </>
    );
};
